import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { CatVideo } from '@/mocks/catVideos';
import VideoComments from './VideoComments';
import {
  useVideoStats,
  formatViewCount,
  formatWatchTime,
} from '@/hooks/useVideoStats';

interface CleanVideoPlayerProps {
  video: CatVideo;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export default function CleanVideoPlayer({
  video,
  onClose,
  onPrevious,
  onNext,
  hasNext = false,
  hasPrevious = false,
}: CleanVideoPlayerProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { stats, currentSessionTime, isTracking, startTracking, stopTracking } = useVideoStats(video.id);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Lock body scroll
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, []);

  // Reset loading state when video changes
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, [video.id]);

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    startTracking();
  }, [startTracking]);

  const handleClose = useCallback(() => {
    stopTracking();
    onClose();
  }, [stopTracking, onClose]);

  const handleNext = useCallback(() => {
    if (hasNext && onNext) {
      stopTracking();
      onNext();
    }
  }, [hasNext, stopTracking, onNext]);

  const handlePrevious = useCallback(() => {
    if (hasPrevious && onPrevious) {
      stopTracking();
      onPrevious();
    }
  }, [hasPrevious, stopTracking, onPrevious]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrevious, handleClose, hasNext, hasPrevious]);

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && hasNext) {
      handleNext();
    } else if (isRightSwipe && hasPrevious) {
      handlePrevious();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);

  const embedUrl = `https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm overflow-y-auto"
        onClick={handleClose}
      >
        <div className="min-h-screen flex items-start justify-center p-4 py-8">
          <motion.div
            ref={containerRef}
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="relative w-full max-w-6xl my-8"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Watercolor Border Frame */}
            <div className="relative">
              {/* Outer watercolor effect */}
              <div className="absolute -inset-4 rounded-3xl opacity-60" 
                style={{
                  background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 25%, #7dd3fc 50%, #38bdf8 75%, #0ea5e9 100%)',
                  filter: 'blur(8px)',
                }}
              />
              
              {/* Inner frame */}
              <div className="relative bg-gradient-to-br from-sky-100 via-blue-50 to-cyan-100 rounded-2xl p-1 shadow-2xl">
                <div className="bg-white rounded-xl overflow-hidden shadow-inner">
                  
                  {/* Top Bar */}
                  <div className="relative z-20 flex items-center justify-between px-4 md:px-6 py-3 md:py-4 bg-gradient-to-r from-sky-50 to-blue-50 border-b border-sky-200">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="flex items-center gap-2 bg-sky-100 border border-sky-200 rounded-full px-3 py-1.5">
                        <i className="ri-play-circle-fill text-sky-600 text-lg"></i>
                        <span className="font-heading text-sky-900 text-sm font-bold hidden sm:inline">MeowCentral</span>
                      </div>
                      {isTracking && currentSessionTime > 0 && (
                        <div className="flex items-center gap-1.5 bg-rose-100 border border-rose-200 rounded-full px-3 py-1.5">
                          <span className="w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
                          <span className="text-rose-700 text-xs font-body font-medium">{formatWatchTime(currentSessionTime)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowInfo(!showInfo);
                        }}
                        className="w-9 h-9 flex items-center justify-center bg-sky-100 hover:bg-sky-200 border border-sky-200 rounded-full transition-colors cursor-pointer"
                      >
                        <i className={`${showInfo ? 'ri-eye-off-line' : 'ri-information-line'} text-sky-700 text-base`} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClose();
                        }}
                        className="w-9 h-9 flex items-center justify-center bg-sky-100 hover:bg-sky-200 border border-sky-200 rounded-full transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-sky-700 text-lg" />
                      </button>
                    </div>
                  </div>

                  {/* Video Container */}
                  <div className="relative aspect-video bg-black">
                    {isLoading && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 z-10">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-12 h-12 border-3 border-sky-300/30 border-t-sky-500 rounded-full mb-3"
                        />
                        <p className="text-sky-300 font-body text-sm">Loading video...</p>
                      </div>
                    )}

                    <iframe
                      ref={iframeRef}
                      src={embedUrl}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      onLoad={handleIframeLoad}
                      loading="eager"
                    />

                    {/* Navigation Arrows - Prominent */}
                    {hasPrevious && (
                      <motion.button
                        whileHover={{ scale: 1.1, x: -4 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrevious();
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-16 h-16 bg-white/95 hover:bg-white rounded-full flex items-center justify-center transition-all cursor-pointer z-10 shadow-2xl border-2 border-sky-200"
                      >
                        <i className="ri-arrow-left-s-line text-sky-600 text-4xl"></i>
                      </motion.button>
                    )}
                    {hasNext && (
                      <motion.button
                        whileHover={{ scale: 1.1, x: 4 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNext();
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-16 h-16 bg-white/95 hover:bg-white rounded-full flex items-center justify-center transition-all cursor-pointer z-10 shadow-2xl border-2 border-sky-200"
                      >
                        <i className="ri-arrow-right-s-line text-sky-600 text-4xl"></i>
                      </motion.button>
                    )}

                    {/* Keyboard hint */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 hidden md:flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2">
                      <i className="ri-arrow-left-line text-white/70 text-sm"></i>
                      <span className="text-white/70 text-xs font-body">Use arrow keys to navigate</span>
                      <i className="ri-arrow-right-line text-white/70 text-sm"></i>
                    </div>
                  </div>

                  {/* Video Info Panel */}
                  <AnimatePresence>
                    {showInfo && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="p-4 md:p-6 bg-gradient-to-b from-white to-sky-50/30">
                          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-3 flex-wrap">
                                <span className="inline-block px-3 py-1 bg-sky-100 text-sky-700 border border-sky-200 rounded-full text-xs font-body font-medium capitalize">
                                  {video.category}
                                </span>
                              </div>
                              <h3 className="font-heading text-gray-900 text-xl md:text-2xl font-bold mb-2 leading-tight">
                                {video.title}
                              </h3>
                              <p className="text-gray-600 font-body text-sm md:text-base mb-4 leading-relaxed">
                                {video.description}
                              </p>
                              
                              {/* Stats */}
                              <div className="flex flex-wrap items-center gap-3 text-gray-600 font-body text-sm">
                                <div className="flex items-center gap-2 bg-sky-50 rounded-full px-3 py-1.5 border border-sky-100">
                                  <i className="ri-eye-line text-sky-600"></i>
                                  <span className="text-gray-900 font-medium">{formatViewCount(stats.viewCount)}</span>
                                </div>
                                <div className="flex items-center gap-2 bg-sky-50 rounded-full px-3 py-1.5 border border-sky-100">
                                  <i className="ri-time-line text-sky-600"></i>
                                  <span>{video.duration}</span>
                                </div>
                                <div className="flex items-center gap-2 bg-amber-50 rounded-full px-3 py-1.5 border border-amber-100">
                                  <i className="ri-timer-line text-amber-600"></i>
                                  <span className="text-gray-900 font-medium">{formatWatchTime(stats.totalWatchTime)}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsLiked(!isLiked);
                                }}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all cursor-pointer whitespace-nowrap text-sm font-body font-medium ${
                                  isLiked
                                    ? 'bg-rose-500 text-white shadow-lg shadow-rose-200'
                                    : 'bg-sky-100 text-sky-700 hover:bg-sky-200 border border-sky-200'
                                }`}
                              >
                                <i className={isLiked ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                                <span>{isLiked ? 'Liked!' : 'Like'}</span>
                              </button>
                              <button className="w-10 h-10 bg-sky-100 hover:bg-sky-200 border border-sky-200 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                                <i className="ri-share-line text-sky-700 text-lg"></i>
                              </button>
                            </div>
                          </div>

                          {/* Comments */}
                          <VideoComments videoId={video.id} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Bottom Navigation Bar */}
                  <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-gradient-to-r from-sky-50 to-blue-50 border-t border-sky-200">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsLiked(!isLiked);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-all cursor-pointer whitespace-nowrap text-xs font-body font-medium ${
                          isLiked
                            ? 'bg-rose-100 text-rose-700 border border-rose-200'
                            : 'bg-white text-gray-600 hover:bg-sky-100 border border-sky-200'
                        }`}
                      >
                        <i className={isLiked ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                        <span className="hidden sm:inline">{isLiked ? 'Liked' : 'Like'}</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowInfo(!showInfo);
                        }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-sky-100 text-gray-600 border border-sky-200 rounded-full transition-all cursor-pointer whitespace-nowrap text-xs font-body font-medium"
                      >
                        <i className="ri-chat-3-line"></i>
                        <span className="hidden sm:inline">Comments</span>
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasPrevious && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrevious();
                          }}
                          className="flex items-center gap-1 px-3 py-2 bg-white hover:bg-sky-100 text-gray-600 border border-sky-200 rounded-full transition-all cursor-pointer whitespace-nowrap text-xs font-body"
                        >
                          <i className="ri-skip-back-line"></i>
                          <span className="hidden sm:inline">Previous</span>
                        </button>
                      )}
                      {hasNext && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNext();
                          }}
                          className="flex items-center gap-1 px-3 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-full transition-all cursor-pointer whitespace-nowrap text-xs font-body font-medium"
                        >
                          <span className="hidden sm:inline">Next</span>
                          <i className="ri-skip-forward-line"></i>
                        </button>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}